import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["exec"];
import { isAssignAction } from './util';
export class Interpreter {
  constructor(machine, initialContext = {}) {
    this.onTransitionCallbacks = [];
    this.running = false;
    this.listeners = new Set();
    this.machine = machine;
    this.value = machine.initialState;
    this.onTransitionCallbacks = [];
    const fullInitialContext = Object.assign({}, machine.context, initialContext);
    this.context = fullInitialContext;
    this.updateContext(fullInitialContext);
    this.currentSnapshot = {
      value: this.value,
      context: this.context
    };
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  getSnapshot() {
    return this.currentSnapshot;
  }
  start() {
    if (this.isRunning()) {
      throw new Error('Machine Interpreter is already running');
    }
    this.machine.start();
    this.running = true;
    this.handleImplicitTransitions();
  }
  stop() {
    this.machine.stop();
    this.running = false;
  }
  send(providedEvent) {
    let eventObject;
    if (typeof providedEvent === 'string') {
      eventObject = {
        type: providedEvent
      };
    } else {
      eventObject = providedEvent;
    }
    const _this$machine$transit = this.machine.transition(this.value, eventObject),
      {
        exec
      } = _this$machine$transit,
      transitionData = _objectWithoutPropertiesLoose(_this$machine$transit, _excluded);
    const prevValue = this.value;
    this.value = transitionData.state;
    const newContext = this.evaluateActions(transitionData.event, transitionData.actions || [], this.context);
    this.updateContext(newContext);

    /**
     * If we are in a service state, we need to evaluate the service and then
     * transition to the next state.
     */
    if (exec) {
      /**
       * If we're already in the same invokable service state, we don't need to
       * do anything because it's already running. So this is a noop.
       */
      if (prevValue === transitionData.state) {
        return this.finalizeServiceEntry();
      }
      this.finalizeServiceEntry();
      this.evaluateService(exec).catch(error => {
        console.error('Could not evaluate fsm service');
        console.error(error);
      });
    } else {
      this.finalizeTransition();
    }
  }
  evaluateService(serviceExec) {
    return serviceExec().then(serviceData => {
      const {
        state,
        actions,
        event
      } = serviceData;
      const newServiceContext = this.evaluateActions(event, actions || [], this.context);
      this.updateContext(newServiceContext);
      this.value = state;
      this.finalizeTransition();
    }).catch(error => {
      console.error('Could not evaluate fsm service');
      console.error(error);
    });
  }
  onTransition(callback) {
    this.onTransitionCallbacks.push(callback);
  }

  /**
   * When a service state is entered, we want to call our on transition callbacks,
   * and potentially other stuff in the future, so that they get an update that we've
   * entered this new service state and the UI or any other observer can react accordingly.
   */
  finalizeServiceEntry() {
    this.handleOnTransitionCallbacks();
  }
  finalizeTransition() {
    this.handleOnTransitionCallbacks();
    this.handleImplicitTransitions();
  }

  /**
   * When we're in an invokable service or eventless transition state we want to kick off the
   * evaluation of that state after we've completed the current transition.
   */
  handleImplicitTransitions() {
    const transitionData = this.machine.implicitTransition(this.value);
    if (!transitionData) {
      return;
    }
    if (transitionData.exec) {
      this.evaluateService(transitionData.exec).catch(error => {
        console.error('Could not evaluate fsm service');
        console.error(error);
      });
    } else if (transitionData.state !== this.value) {
      this.value = transitionData.state;
      const newContext = this.evaluateActions(transitionData.event, transitionData.actions || [], this.context);
      this.updateContext(newContext);
      this.finalizeTransition();
    }
  }
  handleOnTransitionCallbacks() {
    this.currentSnapshot = {
      value: this.value,
      context: this.context
    };
    this.onTransitionCallbacks.forEach(callback => {
      callback(this);
    });
    this.listeners.forEach(listener => listener());
  }
  evaluateActions(event, actions, context) {
    let newContext = Object.assign({}, context);
    if (!actions) {
      return newContext;
    }
    actions.forEach(action => {
      const actionResult = action.exec(newContext, event);
      if (isAssignAction(actionResult)) {
        const dataToMerge = actionResult.data || {};
        newContext = Object.assign({}, newContext, dataToMerge);
      }
    });
    return newContext;
  }
  matches(state) {
    return this.value === state;
  }
  hasTag(tag) {
    return this.machine.hasTag(this.value, tag);
  }
  hasTagForState(value, tag) {
    return this.machine.hasTag(value, tag);
  }
  isRunning() {
    return this.running;
  }
  updateContext(newContext) {
    this.context = newContext;
    this.machine.setContext(newContext);
  }
  mergeContext(newContext = {}) {
    this.updateContext(Object.assign({}, this.context, newContext));
  }
  get history() {
    return this.machine.inspector.getTimeline();
  }
  getInspector() {
    return this.machine.inspector;
  }
}